// Date formatting utility functions

/**
 * Parse a date-only value without letting the timezone shift the calendar day.
 * DATE columns arrive from the API as "YYYY-MM-DD" or, once serialized through
 * JSON, as UTC midnight ("YYYY-MM-DDT00:00:00.000Z"). Passing those to
 * `new Date()` yields UTC midnight, which formats to the previous day in any
 * negative-offset (e.g. US) timezone. For those cases we rebuild the date from
 * its calendar parts at local midnight; genuine timestamps are left untouched.
 */
export function parseDateOnly(dateString) {
  if (!dateString) return null;

  if (dateString instanceof Date) return dateString;

  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.0+)?Z?)?$/.exec(dateString);
  if (match) {
    // setFullYear rather than the Date(y, m, d) constructor, which maps years
    // 0-99 to 1900-1999 (e.g. "0002-10-08" would become 1902-10-08).
    const date = new Date(0, 0, 1);
    date.setFullYear(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return date;
  }

  return new Date(dateString);
}

/**
 * Convert a date-only value to the "YYYY-MM-DD" string an <input type="date">
 * expects, using local calendar parts so it matches what formatDate displays.
 */
export function toDateInputValue(dateString) {
  const date = parseDateOnly(dateString);
  if (!date || isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Bounds for every <input type="date">. Without a max, Chrome accepts up to
// six-digit years (e.g. 20002); with one it stops the year at four digits.
export const DATE_INPUT_MIN = '1900-01-01';
export const DATE_INPUT_MAX = '9999-12-31';

/**
 * True for a complete "YYYY-MM-DD" value from a date input with a four-digit
 * year in [1900, 9999]. Rejects the partial years an input reports mid-typing
 * ("0202-…") and oversized ones ("20002-…").
 */
export function isValidDateInputValue(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  return Boolean(match) && Number(match[1]) >= 1900;
}

/**
 * Opens the browser's calendar popup for an <input type="date">. Native date
 * inputs only open it from the small calendar icon. showPicker throws without
 * user activation and is missing on older browsers; either way the field still
 * works as a typed input.
 */
export function openDatePicker(event) {
  try {
    event.currentTarget.showPicker?.();
  } catch {
    /* Picker unavailable - fall back to the browser default behavior. */
  }
}

// Inputs that already had focus when the current click started.
const focusedAtMouseDown = new WeakSet();

/**
 * Spread onto an <input type="date"> so clicking anywhere in it opens the
 * calendar, while typing still works. While the popup is open it takes the
 * keyboard, so it only opens when the click brings focus into the field: once
 * the field is focused (popup dismissed with Esc, or tabbed in), clicking a
 * month/day/year segment places the cursor for typing instead of reopening it.
 */
export const datePickerTriggers = {
  onMouseDown: (event) => {
    if (document.activeElement === event.currentTarget) {
      focusedAtMouseDown.add(event.currentTarget);
    } else {
      focusedAtMouseDown.delete(event.currentTarget);
    }
  },
  onClick: (event) => {
    if (!focusedAtMouseDown.has(event.currentTarget)) {
      openDatePicker(event);
    }
  }
};

export function formatDate(dateString) {
  if (!dateString) return '-';

  const date = parseDateOnly(dateString);
  if (!date || isNaN(date.getTime())) return '-';

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export function formatDateTime(dateString) {
  if (!dateString) return '-';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function formatRelativeDate(dateString) {
  if (!dateString) return '-';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  
  const now = new Date();
  const diffInMs = now - date;
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
  
  return `${Math.floor(diffInDays / 365)} years ago`;
}

export function isOverdue(dueDateString) {
  if (!dueDateString) return false;

  const dueDate = parseDateOnly(dueDateString);
  const now = new Date();
  if (!dueDate || isNaN(dueDate.getTime())) return false;
  
  // Set time to start of day for comparison
  dueDate.setHours(23, 59, 59, 999);
  now.setHours(0, 0, 0, 0);
  
  return dueDate < now;
}

export function formatDueDateRelative(dueDateString) {
  if (!dueDateString) return '-';
  
  const dueDate = new Date(dueDateString);
  if (isNaN(dueDate.getTime())) return '-';
  
  const now = new Date();
  const timeDiff = dueDate.getTime() - now.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  
  // If within the next 7 days, show relative format
  if (daysDiff >= 0 && daysDiff <= 7) {
    if (daysDiff === 0) return 'Today';
    if (daysDiff === 1) return 'Tomorrow';
    return `In ${daysDiff} days`;
  }
  
  // Otherwise show formatted date
  return dueDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export function isDueSoon(dueDateString, daysThreshold = 7) {
  if (!dueDateString) return false;

  const dueDate = parseDateOnly(dueDateString);
  const now = new Date();
  if (!dueDate || isNaN(dueDate.getTime())) return false;
  const threshold = new Date();
  threshold.setDate(now.getDate() + daysThreshold);
  
  // Set time to start of day for comparison
  dueDate.setHours(23, 59, 59, 999);
  now.setHours(0, 0, 0, 0);
  threshold.setHours(23, 59, 59, 999);
  
  return dueDate >= now && dueDate <= threshold;
}